import { useState, useMemo } from "react"
import { useOutletContext } from "react-router-dom"
import { AlertTriangle } from "lucide-react"
import { differenceInDays } from "date-fns"
import ToolShell from "../components/ToolShell"
import FormulaBox from "../components/FormulaBox"
import ToolFAQ from "../components/ToolFAQ"
import CTABox from "../components/CTABox"
import RelatedToolsList from "../components/RelatedToolsList"
import { ToolDef } from "../toolRegistry"

type FeeType = "flat" | "pct"

export default function LateFeeCalculatorPage() {
  const { tool, currency } = useOutletContext<{ tool: ToolDef, currency: string }>()

  const [monthlyRent, setMonthlyRent] = useState("")
  const [dueDate, setDueDate] = useState("")
  const [paymentDate, setPaymentDate] = useState("")
  const [gracePeriod, setGracePeriod] = useState("5")
  const [feeType, setFeeType] = useState<FeeType>("flat")
  const [feeAmount, setFeeAmount] = useState("")

  const results = useMemo(() => {
    const rent = parseFloat(monthlyRent)
    const fee = parseFloat(feeAmount)
    if (!rent || !dueDate || !paymentDate || !fee) return null

    const due = new Date(dueDate)
    const paid = new Date(paymentDate)
    const daysAfterDue = differenceInDays(paid, due)
    const grace = parseInt(gracePeriod) || 0
    const daysLate = Math.max(0, daysAfterDue - grace)

    const lateFee = daysLate > 0
      ? (feeType === "flat" ? fee : rent * (fee / 100))
      : 0

    const totalDue = rent + lateFee

    return { daysAfterDue, daysLate, lateFee, totalDue, onTime: daysLate === 0 }
  }, [monthlyRent, dueDate, paymentDate, gracePeriod, feeType, feeAmount])

  const fmt = (n: number) => `${currency}${n.toFixed(2)}`

  return (
    <div className="space-y-8">
      <ToolShell>
        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Input Panel */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Payment Details</p>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Monthly Rent</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{currency}</span>
                  <input
                    type="number"
                    min="0"
                    value={monthlyRent}
                    onChange={e => setMonthlyRent(e.target.value)}
                    placeholder="e.g. 1200"
                    className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Rent Due Date</label>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={e => setDueDate(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Payment Date</label>
                  <input
                    type="date"
                    value={paymentDate}
                    onChange={e => setPaymentDate(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Grace Period (days)</label>
                <input
                  type="number"
                  min="0"
                  value={gracePeriod}
                  onChange={e => setGracePeriod(e.target.value)}
                  placeholder="e.g. 5"
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Late Fee Type</label>
                <select
                  value={feeType}
                  onChange={e => setFeeType(e.target.value as FeeType)}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  <option value="flat">Flat Fee ({currency})</option>
                  <option value="pct">Percentage of Rent (%)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  {feeType === "flat" ? "Late Fee Amount ({currency})" : "Late Fee Rate (%)"}
                </label>
                <div className="relative">
                  {feeType === "flat" && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{currency}</span>}
                  {feeType === "pct" && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>}
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={feeAmount}
                    onChange={e => setFeeAmount(e.target.value)}
                    placeholder={feeType === "flat" ? "e.g. 50" : "e.g. 5"}
                    className={`w-full rounded-lg border border-slate-200 bg-white ${feeType === "flat" ? "pl-7" : "pl-3"} ${feeType === "pct" ? "pr-8" : "pr-3"} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40`}
                  />
                </div>
              </div>
            </div>

            {/* Result Panel */}
            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5 space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Results</p>

              {!results ? (
                <p className="text-slate-400 text-sm text-center py-10">Enter rent, dates, and fee details to calculate</p>
              ) : results.onTime ? (
                <div className="text-center py-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                    <span className="text-2xl">✓</span>
                  </div>
                  <p className="text-xl font-bold text-green-600">No Late Fee</p>
                  <p className="text-sm text-slate-500 mt-2">Payment received within the grace period</p>
                </div>
              ) : (
                <>
                  <div className="text-center py-3">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Late Fee Owed</p>
                    <p className="text-5xl font-extrabold text-red-500">{fmt(results.lateFee)}</p>
                  </div>

                  <div className="border-t border-primary/10 pt-4 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Days Late (after grace)</span>
                      <span className="font-semibold text-slate-900">{results.daysLate} day{results.daysLate !== 1 ? "s" : ""}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Monthly Rent</span>
                      <span className="font-semibold text-slate-900">{fmt(parseFloat(monthlyRent))}</span>
                    </div>
                    <div className="flex justify-between text-sm font-semibold border-t border-primary/10 pt-3">
                      <span className="text-slate-700">Total Amount Due</span>
                      <span className="text-slate-900 text-lg">{fmt(results.totalDue)}</span>
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-700">
                    <strong>UK Note:</strong> Late fees must be specified in the tenancy agreement and be reasonable. Excessive fees may be unenforceable.
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </ToolShell>

      <FormulaBox
        formula={`Days Late = Payment Date − Due Date − Grace Period\n\nIf Days Late > 0:\n  Flat fee: Late Fee = Fixed Amount\n  % fee: Late Fee = Monthly Rent × (Rate ÷ 100)\n\nTotal Due = Monthly Rent + Late Fee`}
        description="The grace period is the number of days after the due date before the fee applies. A 5-day grace period means a fee only applies if payment is received 6+ days late."
      />

      <ToolFAQ
        items={[
          {
            q: "Are late fees legal in the UK?",
            a: "Late fees can be charged in the UK, but only if they are clearly stated in the tenancy agreement. They must be reasonable and proportionate. The Tenant Fees Act 2019 caps certain charges, so always ensure your fee clause complies with current legislation.",
          },
          {
            q: "How much can I charge as a late fee?",
            a: "There is no fixed legal cap on late fees in England for ASTs, but they must be 'reasonably incurred' and specified in writing. A flat fee of {currency}25–{currency}50 or 3–5% of monthly rent is typical. Excessive fees can be challenged and may be unenforceable.",
          },
          {
            q: "What should I do if a tenant repeatedly pays late?",
            a: "Document every late payment in writing with dates. After repeated incidents, you may serve a formal notice. Persistent late payment can be grounds for ending the tenancy. Proactive communication and flexible payment options often resolve issues before escalation.",
          },
        ]}
      />

      <CTABox
        icon={AlertTriangle}
        heading="Tracking late payments manually?"
        body={tool.ctaText}
        ctaLabel="Start free"
        ctaHref="/register"
      />

      <RelatedToolsList relatedSlugs={tool.related} />
    </div>
  )
}
